import { useQuery } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { fetchMfaStatus, mfaIsDeferred } from '@/features/Auth/gcp/emailMfa';
import { securitySetupUrl } from '@/features/Auth/mfaReturn';
import { PROFILE_STALE_TIME } from '@/shared/config/queryDefaults';

/**
 * Persistent "Secure your account" prompt for a user who has not enrolled a
 * second factor yet.
 *
 * Shown ONLY when the backend says the environment defers enrolment to the
 * first regulated action (MFA_ENFORCEMENT_MODE=before_regulated_action) AND
 * the account has no factor. In the default mode an unenrolled user never gets
 * this far -- the router sends them to /security-setup first -- so the banner
 * renders nothing there. No dismiss button, on purpose: the control is not
 * optional, only its timing is, and a banner that can be closed becomes a
 * reminder nobody sees again.
 *
 * Shares the `['auth','mfa','status']` query with the router gate, so it costs
 * no extra request. A `degraded` answer (provider unreachable) hides it: that
 * response reports "enrolled" because it cannot tell, and nagging on a guess
 * would be wrong.
 */
export function SecureAccountBanner() {
  const { data } = useQuery({
    queryKey: ['auth', 'mfa', 'status'],
    queryFn: fetchMfaStatus,
    staleTime: PROFILE_STALE_TIME,
    retry: false,
  });

  if (!data || data.enrolled || data.degraded || !mfaIsDeferred(data)) {
    return null;
  }

  const here =
    typeof window === 'undefined'
      ? null
      : `${window.location.pathname}${window.location.search}`;

  return (
    <div
      role="status"
      data-name="SecureAccountBanner"
      className="flex shrink-0 items-center justify-between gap-4 border-b border-[#E9D5FF] bg-[#F5F0FF] px-6 py-2 text-[13px] text-[#121212]"
    >
      <span className="flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-[#7F22FE]" aria-hidden />
        <span>
          <strong>Secure your account.</strong> Turn on two-step sign-in to add
          clients, open accounts, move money or trade.
        </span>
      </span>
      <a
        href={securitySetupUrl(here)}
        className="whitespace-nowrap font-bold text-[#7F22FE] hover:underline"
      >
        Set up now
      </a>
    </div>
  );
}
